import React from "react";
import { PieChart, Pie, Cell } from "recharts";

const RADIAN = Math.PI / 180;

type GaugeChartProps = {
  value: number;
  isEvasion?: boolean;
};

const colors = [
  "#fa4338",
  "#fd7744",
  "#fea34e",
  "#f4ed6c",
  "#B5DD74",
  "#6BCA70",
  "#00BA66",
];

const cx = 100;
const cy = 130;
const iR = 30;
const oR = 60;

const needle = (
  value: number,
  data: { value: number }[],
  cx: number,
  cy: number,
  iR: number,
  oR: number,
  color: string,
  startAngle = 180,
  endAngle = 0
) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;

  const safeValue = Math.max(0, Math.min(total, value));

  const angle = startAngle + (endAngle - startAngle) * (safeValue / total);

  const length = (iR + 2 * oR) / 3;
  const sin = Math.sin(-RADIAN * angle);
  const cos = Math.cos(-RADIAN * angle);
  const r = 4;
  const x0 = cx;
  const y0 = cy;
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  return (
    <g>
      <circle cx={x0} cy={y0} r={r} fill={color} stroke="none" />
      <path d={`M${xba} ${yba} L${xbb} ${ybb} L${xp} ${yp} Z`} fill={color} />
    </g>
  );
};

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  isEvasion = false,
}) => {
  const data = React.useMemo(
    () => colors.map(() => ({ value: 100 / colors.length })),
    []
  );

  const cellColors = isEvasion ? [...colors].reverse() : colors;

  const total = data.reduce((s, d) => s + d.value, 0);
  const safeValue = Math.max(0, Math.min(total, value));

  return (
    <PieChart width={200} height={160} style={{ marginTop: "-30px" }}>
      <Pie
        dataKey="value"
        startAngle={180}
        endAngle={0}
        data={data}
        cx={cx}
        cy={cy}
        innerRadius={iR}
        outerRadius={oR}
        stroke="none"
        isAnimationActive={false}
        style={{ pointerEvents: "none" }}
      >
        {cellColors.map((color, index) => (
          <Cell key={`cell-${index}`} fill={color} />
        ))}
      </Pie>

      {needle(safeValue, data, cx, cy, iR, oR, "#2c3e50")}
    </PieChart>
  );
};
